<template>
  <div>
    <!-- بخش تنظیمات چیدمان، سایز و تم کارت -->
    <v-card class="mb-5 pa-4 rounded-xl settings-card" elevation="1" outlined>
      <div class="d-flex align-center justify-space-between mb-3">
        <div class="d-flex align-center">
          <v-icon color="primary" class="me-2">mdi-tune-variant</v-icon>
          <span class="font-weight-bold text-subtitle-2 grey--text text--darken-3">
            تنظیمات نمایش و استایل کارت‌ها
          </span>
        </div>
      </div>

      <v-divider class="mb-4"></v-divider>

      <v-row dense align="center">
        <!-- ۲. انتخاب نوع چیدمان (لیستی، آکاردئون، اسلایدری) -->
        <v-col cols="12" sm="5" md="5" class="mb-3 mb-sm-0">
          <div class="text-caption grey--text text--darken-1 mb-2 font-weight-medium">
            حالت چیدمان:
          </div>
          <v-btn-toggle
            v-model="displayType"
            mandatory
            dense
            color="primary"
            class="toggle-btn-group w-100"
            @change="emitChanges"
          >
            <v-btn
              v-for="layout in layoutOptions"
              :key="layout.value"
              :value="layout.value"
              class="flex-grow-1"
              small
            >
              <v-icon small class="me-1">{{ layout.icon }}</v-icon>
              {{ layout.label }}
            </v-btn>
          </v-btn-toggle>
        </v-col>

        <!-- ۳. انتخاب سایز / مقیاس -->
        <v-col cols="12" sm="3" md="3">
          <div class="d-flex justify-space-between align-center mb-2">
            <span class="text-caption grey--text text--darken-1 font-weight-medium">
              اندازه:
            </span>
            <span class="text-caption font-weight-bold primary--text">{{ currentScaleLabel }}</span>
          </div>
          <v-btn-toggle
            v-model="sizeLevel"
            mandatory
            dense
            color="primary"
            class="toggle-btn-group w-100"
            @change="emitChanges"
          >
            <v-btn
              v-for="item in gridOptions"
              :key="item.value"
              :value="item.value"
              class="flex-grow-1"
              small
            >
              <v-icon :size="item.size">{{ item.icon }}</v-icon>
            </v-btn>
          </v-btn-toggle>
        </v-col>
      </v-row>
    </v-card>

    <!-- دکمه کارت‌های بایگانی شده -->
    <v-btn
      v-if="archivedCards && archivedCards.length > 0"
      class="btn-buy rounded-lg mb-6"
      block
      elevation="2"
      @click="openArchiveDialog"
    >
      <v-icon left>mdi-archive-arrow-down-outline</v-icon>
      کارت‌های بایگانی شده ({{ archivedCards.length }})
    </v-btn>

    <!-- لیست کارت‌های فعال لوکال -->
    <v-row v-if="localCards.length > 0" class="mb-4">
      <v-col
        v-for="(card, index) in localCards"
        :key="card.id || index"
        :class="{ dragging: draggingIndex === index }"
        class="drag-item-wrapper"
        cols="12"
        draggable="true"
        @dragend="onDragEnd"
        @dragover="onDragOver"
        @dragstart="onDragStart(index, $event)"
        @drop="onDrop(index)"
      >
        <v-card class="pa-3 hover-card rounded-lg" outlined>
          <div class="d-flex align-center">
            <div class="d-flex align-center mr-2 ml-md-3 ml-1 grab-handle">
              <v-icon color="grey darken-1">mdi-drag-vertical</v-icon>
              <v-chip class="mr-1 font-weight-bold" color="primary" label x-small>
                {{ index + 1 }}
              </v-chip>
            </div>

            <div class="flex-grow-1" style="min-width: 0">
              <div class="d-flex justify-space-between align-center">
                <div style="flex: 1; min-width: 0">
                  <div class="font-weight-bold text-subtitle-1 mb-1 text-truncate d-flex align-center">
                    <img
                      v-if="card.bank_logo"
                      :src="card.bank_logo"
                      alt="logo"
                      class="me-2"
                      style="width: 22px; height: 22px; object-fit: contain"
                    />
                    {{ card.bank_name || 'کیوکارت' }}
                  </div>

                  <div class="grey--text text-body-2 d-flex flex-wrap align-center">
                    <span class="text-caption ltr-text font-weight-medium">
                      {{ formatCardNumber(card.cart_number) }}
                    </span>
                    <span v-if="card.owner_name" class="mx-2 grey--text text--lighten-1">|</span>
                    <span v-if="card.owner_name" class="text-caption">
                      {{ card.owner_name }}
                    </span>
                  </div>
                </div>

                <!-- اکشن‌ها -->
                <div class="d-flex align-center mr-2">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn color="primary" icon small v-bind="attrs" v-on="on" @click="openEditMode(card, index)">
                        <v-icon small>mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    <span>ویرایش کارت</span>
                  </v-tooltip>

                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn color="amber darken-3" icon small v-bind="attrs" v-on="on" @click="archiveCard(index)">
                        <v-icon small>mdi-archive-arrow-down-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>بایگانی کارت</span>
                  </v-tooltip>

                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn color="error" icon small v-bind="attrs" v-on="on" @click="deleteCard(index)">
                        <v-icon small>mdi-delete-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>حذف کارت</span>
                  </v-tooltip>
                </div>
              </div>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <div v-else class="text-center grey--text my-10 py-8">
      <v-icon color="grey lighten-2" size="64">mdi-credit-card-off-outline</v-icon>
      <div class="mt-3 font-weight-medium">هیچ کارتی ثبت نشده است.</div>
    </div>

    <!-- پاپ‌آپ تک مرحله‌ای اطلاعات بانکی -->
    <custom-popup
      id="add-card"
      v-model="showAddCard"
      :cons="cons"
      max-width="650"
      :hideConfirm="true"
      :persistent="true"
    >
      <v-card flat class="pa-2 pa-sm-4">
        <v-form ref="cardForm" lazy-validation @submit.prevent="handleSubmit">
          <v-row dense>
            <v-col cols="12" sm="6" class="pa-2">
              <v-text-field
                :rules="[rules.required, rules.cardNumber]"
                :value="form.cart_number"
                append-icon="mdi-credit-card"
                dense
                dir="ltr"
                label="شماره کارت"
                maxlength="19"
                outlined
                placeholder="۱۶ رقم"
                @input="handleCardInput"
              />
            </v-col>

            <v-col cols="12" sm="6" class="pa-2">
              <v-text-field
                v-model="form.bank_name"
                :rules="[rules.required, rules.minLen]"
                dense
                label="نام بانک"
                outlined
              >
                <template v-slot:append>
                  <img
                    v-if="bankLogo"
                    :src="bankLogo"
                    alt="logo"
                    style="width: 24px; height: 24px; object-fit: contain"
                  />
                  <v-icon v-else>mdi-bank</v-icon>
                </template>
              </v-text-field>
            </v-col>

            <v-col cols="12" sm="6" class="pa-2">
              <v-text-field
                :value="form.shaba_number"
                @input="handleShebaInput"
                :rules="[rules.required, rules.sheba]"
                dense
                label="شماره شبا (بدون IR)"
                outlined
                dir="ltr"
                placeholder="...01000"
                maxlength="24"
              >
                <template v-slot:append>
                  <span class="font-weight-bold black--text" style="font-size: 16px">IR</span>
                </template>
              </v-text-field>
            </v-col>

            <v-col cols="12" sm="6" class="pa-2">
              <v-text-field
                :value="form.account_number"
                @input="(v) => handleNumericInput('account_number', english(v))"
                :rules="[rules.required, rules.numeric]"
                append-icon="mdi-notebook"
                dense
                label="شماره حساب"
                outlined
                dir="ltr"
              />
            </v-col>

            <v-col cols="12" class="pa-2">
              <v-text-field
                v-model="form.owner_name"
                :rules="[rules.required, rules.nameOnly]"
                append-icon="mdi-account"
                dense
                label="نام صاحب کارت"
                outlined
              />
            </v-col>

            <v-col cols="12" class="pa-2">
              <v-textarea
                v-model="form.description"
                append-icon="mdi-text"
                dense
                rows="2"
                label="توضیحات (اختیاری)"
                outlined
                hide-details="auto"
              />
            </v-col>
          </v-row>

          <div class="d-flex justify-end mt-4">
            <v-btn class="me-2 btn-delete rounded-lg" @click="showAddCard = false">              
              <v-icon right small>mdi-close</v-icon>
انصراف</v-btn>
            <v-spacer/>
            <v-btn class="btn-get-code rounded-lg" elevation="1" @click="handleSubmit">
              {{ editingIndex !== null ? "ویرایش و ذخیره" : "ثبت کارت" }}
              <v-icon right small>mdi-check</v-icon>
            </v-btn>
          </div>
        </v-form>
      </v-card>
    </custom-popup>

    <v-btn class="py-5 btn-get-code rounded-lg" outlined block @click="openAddMode">
      <v-icon left>mdi-plus</v-icon>
      افزودن کارت جدید
    </v-btn>

    <archived-cards-dialog
      ref="archiveDialog"
      :archived-list="archivedCards"
      @restore="handleRestoreCard"
    />
  </div>
</template>

<script>
import { defineComponent } from "vue";
import { getBankInfoByCardNumber } from "../js/getBankNameByBankNumber.js";
import ArchivedCardsDialog from "./ArchivedAccount.vue";

export default defineComponent({
  name: "ManagementAccount",
  components: {
    ArchivedCardsDialog,
  },

  props: {
    value: {
      type: Object,
      default: () => ({ list: [], displayType: "list", sizeLevel: 2, cardTheme: "white" }),
    },
  },

  data() {
    return {
      localCards: [],
      archivedCards: [],
      showAddCard: false,
      editingIndex: null,
      draggingIndex: -1,
      bankLogo: null,
      cons: { title: "افزودن کارت" },

      displayType: "list",
      sizeLevel: 2,
      cardTheme: "white", // پیش‌فرض سفید

      layoutOptions: [
        { label: "لیستی", value: "list", icon: "mdi-view-day-outline" },
        { label: "آکاردئون", value: "accordion", icon: "mdi-view-agenda-outline" },
        { label: "اسلایدری", value: "slider", icon: "mdi-view-carousel-outline" },
      ],

      gridOptions: [
        { value: 1, icon: "mdi-format-size", size: 19, label: "بزرگ" },
        { value: 2, icon: "mdi-crop-square", size: 17, label: "استاندارد" },
        { value: 3, icon: "mdi-view-module", size: 15, label: "متوسط" },
        { value: 4, icon: "mdi-apps", size: 13, label: "ریز" },
      ],

      form: {
        id: null,
        description: "",
        bank_name: "",
        account_number: "",
        cart_number: "",
        shaba_number: "",
        owner_name: "",
        bank_logo: null,
        bank_color: null,
      },

      rules: {
        required: (v) => !!v || "این فیلد الزامی است",
        numeric: (v) => {
          if (!v) return true;
          const clean = v.toString().replace(/,/g, "").replace(/\s/g, "");
          return /^\d+$/.test(clean) || "فقط عدد وارد کنید";
        },
        nameOnly: (v) =>
          /^[آ-یa-zA-Z\s\u200c()]+$/.test(v) || "فقط حروف و پرانتز وارد کنید",
        cardNumber: (v) => {
          if (!v) return "شماره کارت الزامی است";
          let clean = this.english(v.toString().replace(/\s/g, ""));
          return (
            (clean.length === 16 && /^\d+$/.test(clean)) ||
            "شماره کارت باید ۱۶ رقم باشد"
          );
        },
        sheba: (v) => (v && v.length === 24) || "شماره شبا باید ۲۴ رقم باشد",
        minLen: (v) => (v && v.length >= 2) || "حداقل ۲ کاراکتر وارد کنید",
      },
    };
  },

  computed: {
    currentScaleLabel() {
      const match = this.gridOptions.find((x) => x.value === this.sizeLevel);
      return match ? match.label : "";
    },
  },

  watch: {
    value: {
      handler(newVal) {
        if (newVal) {
          if (Array.isArray(newVal.list)) {
            this.localCards = JSON.parse(JSON.stringify(newVal.list));
          }
          if (newVal.displayType) {
            this.displayType = newVal.displayType;
          }
          if (newVal.sizeLevel) {
            this.sizeLevel = newVal.sizeLevel;
          }
          if (newVal.cardTheme) {
            this.cardTheme = newVal.cardTheme;
          }
        }
      },
      immediate: true,
      deep: true,
    },
  },

  methods: {
    english(value) {
      if (!value) return "";
      return value.toString().replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d));
    },

    formatCardNumber(value) {
      if (!value) return "";
      const rawValue = this.english(value).replace(/\D/g, "");
      const truncated = rawValue.slice(0, 16);
      return truncated.replace(/(\d{4})/g, "$1 ").trim();
    },

    handleCardInput(value) {
      const formatted = this.formatCardNumber(value);
      this.form.cart_number = formatted;
      const cleanDigits = formatted.replace(/\D/g, "");

      if (cleanDigits.length >= 6) {
        try {
          const info = getBankInfoByCardNumber(cleanDigits);
          if (info && info.name) {
            this.form.bank_name = info.name;
            this.bankLogo = info.logo || null;
            this.form.bank_logo = info.logo || null;
            this.form.bank_color = info.color || info.gradient || null;
          }
        } catch (e) {
          console.warn("خطا در تشخیص اطلاعات بانک:", e);
        }
      }
    },

    handleShebaInput(value) {
      let valueEng = this.english(value);
      if (!valueEng) {
        this.form.shaba_number = "";
        return;
      }
      const clean = valueEng.toUpperCase().replace(/IR/g, "").replace(/\D/g, "");
      this.form.shaba_number = clean.slice(0, 24);
    },

    handleNumericInput(key, value) {
      this.form[key] = value ? value.toString().replace(/\D/g, "") : "";
    },

    emitChanges() {
      const updatedObject = {
        ...this.value,
        list: this.localCards,
        displayType: this.displayType,
        sizeLevel: this.sizeLevel,
        cardTheme: this.cardTheme,
      };
      this.$emit("input", updatedObject);
      this.$emit("update:value", updatedObject);
    },

    openAddMode() {
      this.resetForm();
      this.cons.title = "افزودن کارت جدید";
      this.showAddCard = true;
      this.$nextTick(() => {
        if (this.$refs.cardForm) this.$refs.cardForm.resetValidation();
      });
    },

    openEditMode(card, index) {
      this.form = { ...card };
      this.editingIndex = index;
      if (this.form.cart_number) {
        this.form.cart_number = this.formatCardNumber(this.form.cart_number);
        const rawCard = this.form.cart_number.replace(/\D/g, "");
        if (rawCard.length >= 6) {
          const info = getBankInfoByCardNumber(rawCard);
          if (info) this.bankLogo = info.logo;
        }
      }
      this.cons.title = "ویرایش کارت";
      this.showAddCard = true;
    },

    handleSubmit() {
      if (!this.$refs.cardForm.validate()) {
        this.$toast.warning("لطفاً فیلدهای الزامی را به درستی وارد کنید.");
        return;
      }

      const cardData = {
        ...this.form,
        id: this.form.id || Date.now(),
        cart_number: this.english(this.form.cart_number).replace(/\s/g, ""),
        bank_logo: this.bankLogo || this.form.bank_logo,
      };

      if (this.editingIndex !== null) {
        this.$set(this.localCards, this.editingIndex, cardData);
        this.$toast.success("کارت با موفقیت ویرایش شد.");
      } else {
        this.localCards.push(cardData);
        this.$toast.success("کارت با موفقیت اضافه شد.");
      }

      this.emitChanges();
      this.showAddCard = false;
      this.resetForm();
    },

    archiveCard(index) {
      const removed = this.localCards.splice(index, 1)[0];
      this.archivedCards.push(removed);
      this.emitChanges();
      this.$toast.info("کارت به بایگانی منتقل شد.");
    },

  async deleteCard(index) {
      if (await this.$modal.yesNo("آیا از حذف این کیوکارت اطمینان دارید؟")) {
       await this.localCards.splice(index, 1);
      await  this.emitChanges();
       await  this.$toast.success("کیوکارت حذف شد.");
      }
    },

    handleRestoreCard({ index, item }) {
      this.archivedCards.splice(index, 1);
      this.localCards.push(item);
      this.emitChanges();
    },

    openArchiveDialog() {
      if (this.$refs.archiveDialog && this.$refs.archiveDialog.open) {
        this.$refs.archiveDialog.open();
      }
    },

    resetForm() {
      this.form = {
        id: null,
        description: "",
        bank_name: "",
        account_number: "",
        cart_number: "",
        shaba_number: "",
        owner_name: "",
        bank_logo: null,
        bank_color: null,
      };
      this.bankLogo = null;
      this.editingIndex = null;
    },

    onDragStart(index, event) {
      this.draggingIndex = index;
      if (event.dataTransfer) event.dataTransfer.effectAllowed = "move";
    },
    onDragOver(event) {
      event.preventDefault();
      return false;
    },
    onDrop(toIndex) {
      const fromIndex = this.draggingIndex;
      if (fromIndex === -1 || fromIndex === toIndex) return;
      const itemToMove = this.localCards.splice(fromIndex, 1)[0];
      this.localCards.splice(toIndex, 0, itemToMove);
      this.draggingIndex = -1;
      this.emitChanges();
    },
    onDragEnd() {
      this.draggingIndex = -1;
    },
  },
});
</script>

<style scoped>
.settings-card {
  background-color: #fbfcfe;
  border: 1px solid rgba(0, 0, 0, 0.08);
}
.toggle-btn-group {
  display: flex;
  background-color: #f1f3f6;
  border-radius: 8px;
  padding: 2px;
}
.grab-handle {
  cursor: grab;
}
.grab-handle:active {
  cursor: grabbing;
}
.drag-item-wrapper {
  transition: all 0.2s ease;
}
.drag-item-wrapper.dragging {
  opacity: 0.5;
  background-color: #f0f0f0;
}
.ltr-text {
  direction: ltr;
  display: inline-block;
}
</style>
